import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  BarChart3, 
  Search, 
  Menu, 
  Sun, 
  Moon,
  Languages,
  LogOut,
  User as UserIcon
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

interface LayoutProps {
  children: React.ReactNode;
  sidebarContent?: React.ReactNode;
  hideNavigation?: boolean;
  onSearch?: (term: string) => void;
}

export const Layout: React.FC<LayoutProps> = ({ children, sidebarContent, hideNavigation = false, onSearch }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { language, toggleLanguage, t } = useLanguage();
  const { user, logout } = useAuth();
  
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('theme');
      if (stored) return stored === 'dark';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleTheme = () => setIsDarkMode(!isDarkMode);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex font-sans text-slate-900 dark:text-slate-100 transition-colors duration-200">
      {!hideNavigation && isSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm" onClick={() => setIsSidebarOpen(false)} />
      )}

      {!hideNavigation && (
        <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transform transition-all duration-200 ease-in-out ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          <div className="h-20 flex items-center px-8 border-b border-slate-100 dark:border-slate-800">
            <Link to="/" className="flex items-center gap-2 text-2xl font-bold text-indigo-600 dark:text-indigo-400 tracking-tight">
              <div className="w-8 h-8 bg-indigo-600 text-white rounded-lg flex items-center justify-center text-lg shadow-indigo-200 dark:shadow-none shadow-md">
                <BarChart3 size={20} strokeWidth={3} />
              </div>
              MyETRAFFIC
            </Link>
          </div>

          <div className="p-6 space-y-8 overflow-y-auto h-[calc(100vh-10rem)]">
            <div>
              <div className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4 px-2">{t.dashboards}</div>
              <nav className="space-y-1">
                <Link 
                  to="/" 
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                    location.pathname === '/' 
                      ? 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 font-semibold' 
                      : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200'
                  }`}
                  onClick={() => setIsSidebarOpen(false)}
                >
                  <LayoutDashboard size={20} />
                  {t.overview}
                </Link>
              </nav>
            </div>
            {sidebarContent && (
              <div>
                  <div className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider mb-4 px-2">{t.projectMenu}</div>
                  <div className="space-y-1" onClick={() => setIsSidebarOpen(false)}>
                    {sidebarContent}
                  </div>
              </div>
            )}
          </div>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900">
            <div className="flex items-center justify-between gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-2xl">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center text-indigo-600 dark:text-indigo-400 shrink-0">
                  <UserIcon size={18} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-bold text-slate-900 dark:text-white truncate">{user?.displayName || 'User'}</p>
                  <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                </div>
              </div>
              <button onClick={handleLogout} className="p-2 text-slate-400 hover:text-rose-500 transition-colors" title="Log Out"><LogOut size={18} /></button>
            </div>
          </div>
        </aside>
      )}

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {!hideNavigation && (
          <header className="h-16 md:h-20 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between px-4 sm:px-8 sticky top-0 z-30 transition-colors">
            <div className="flex items-center gap-4 w-full">
              <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg shrink-0"><Menu size={24} /></button>
              {onSearch && (
                <div className="hidden md:flex items-center gap-3 bg-slate-50 dark:bg-slate-800 px-4 py-2.5 rounded-2xl w-64 focus-within:ring-2 focus-within:ring-indigo-100 dark:focus-within:ring-indigo-900 transition-all">
                  <Search size={18} className="text-slate-400 dark:text-slate-500" />
                  <input type="text" placeholder={t.searchPlaceholder} onChange={(e) => onSearch(e.target.value)} className="bg-transparent border-none focus:ring-0 text-sm w-full outline-none text-slate-700 dark:text-slate-200 placeholder:text-slate-400" />
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 md:gap-3 shrink-0">
              <button onClick={toggleLanguage} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors border border-slate-200 dark:border-slate-700" title="Switch Language"><Languages size={16} className="text-indigo-500" /><span>{language === 'en' ? 'EN' : 'VN'}</span></button>
              <button onClick={toggleTheme} className="p-2 text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 rounded-xl transition-colors" title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>{isDarkMode ? <Sun size={20} /> : <Moon size={20} />}</button>
            </div>
          </header>
        )}

        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-8 lg:p-12 scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700 flex flex-col">
          <div className="flex-1">
            {children}
          </div>
          <footer className="mt-12 md:mt-16 pt-8 text-center"><p className="text-[10px] md:text-xs font-medium text-slate-400 dark:text-slate-600">Designed By MyESALON</p></footer>
        </div>
      </main>
    </div>
  );
};